import React, {useEffect, useState} from 'react';
import { styled } from '@mui/material/styles';
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";

import { useForm } from "react-hook-form";
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from "yup";

import { axiosDelay } from "../../config/config";

import {
	appSetAlert, appSetModalContent
} from "../../redux/app/appActions";

import {
	dataReloadDependants
} from "../../redux/data/dataActions";

import {useTranslation} from "react-i18next";
import useDataSource from "../../hooks/useDataSource";

import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import InputAdornment from "@mui/material/InputAdornment";
import Button from '@mui/material/Button';
import Input from "@mui/material/Input";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import StarIcon from "@mui/icons-material/Star";
import SubjectIcon from "@mui/icons-material/Subject";
import SortIcon from "@mui/icons-material/Sort";
import FilterListIcon from '@mui/icons-material/FilterList';
import LinearProgress from "@mui/material/LinearProgress";

const PREFIX = 'ProjectHubSetupProject';

const classes = {
    root: `${PREFIX}-root`,
    linearProgress: `${PREFIX}-linearProgress`,
    card: `${PREFIX}-card`,
    form: `${PREFIX}-form`,
    margin: `${PREFIX}-margin`,
    formEmbeddedActions: `${PREFIX}-formEmbeddedActions`
};

const Root = styled('div')((
    {
        theme
    }
) => ({
    [`&.${classes.root}`]: {
		width: '100%',
		height: '100%',
		//overflow: 'hidden'
	},

    [`& .${classes.linearProgress}`]: {
		height: '1px'
	},

    [`& .${classes.card}`]: {
		height: '100%',
		width: '100%'
	},

    [`& .${classes.form}`]: {
		height: '550px'
	},

    [`& .${classes.margin}`]: {
		margin: theme.spacing(1),
		width: 'calc(100% - 16px)'
	},

    [`& .${classes.formEmbeddedActions}`]: {
		// position: 'absolute',
		// bottom: '0px',
		width: 'calc(100% - 32px)'
	}
}));

export default function ProjectHubSetupProject({configuration, xId}) {

	const dispatch = useDispatch();
	const { t } = useTranslation();

	// App State
	const config = useSelector(state => state.config);
	const isThemeLight = useSelector(state => state.persist.themeLight);

	// Component State
	const dataSourceName = configuration?.dataSource?.name ? configuration?.dataSource?.name : '';
	const dataSourceClientName = configuration?.dataSource?.clientNames ? configuration?.dataSource?.clientNames : '';
	const postCreationTask = configuration?.element?.postCreationTask !== undefined ? configuration.element.postCreationTask.enabled : false;

	const dataSource = useDataSource(dataSourceName);
	const dataSourceClients = useDataSource(dataSourceClientName);
	const [showProgress, setShowProgress] = useState(true);
	const [clientValues, setClientValues] = useState([]);
	const [clientValue, setClientValue] = useState();

	useEffect( () => {
		if (dataSourceClients.data) {
			const clients = dataSourceClients.data;
			const clientDD = clients.map(m => {
				return {value: m.clientid, label: m.clientname}
			})
			setClientValues(clientDD);
		}
	},[dataSourceClients.data])

	// Loaded
	useEffect(() => {
		if (dataSource.fetching === false && dataSource.fetched === true) {
			setShowProgress(false);
		}
	}, [dataSource.fetched]);

	const projectIsUnique = async (name) => {
		const projectExists = dataSource.data.find(d => d.projectname.toLowerCase() === name.toLowerCase());
		if (projectExists) {
			return false;
		} else {
			return true;
		}
	};

	const schema = yup.object().shape({
		name: yup.string().required().test("Project must be unique", "Project must be unique", projectIsUnique),
		client: yup.string().required(),
		description: yup.string().required(),
		number: yup.string().required(),
		tasknum: yup.string()
	});

    const resolver = yupResolver(schema);
    const { register, trigger, handleSubmit, formState, formState: { errors } } = useForm({ resolver });


	const onSubmit = dataToPost => {

		setShowProgress(true);

		// TODO: Any additional validation
		const projectExists = dataSource.data.find(d => d.projectname.toLowerCase() === dataToPost.name.toLowerCase());
		if (projectExists) {
			// Close the dialog and signal a reload
			handleClose();
			return;
		}

		createProject(dataToPost);
	};

	const handleClose = () => {
		dispatch(appSetModalContent(null));
	}

	const createProject = dataToPost => {

		// Before we post get the user's ObjectID
		setTimeout(function () {

			const postBody = {
				"projectName": dataToPost.name,
				"clientID": dataToPost.client,
				"projectDesc": dataToPost.description,
				"projectNum": dataToPost.number,
				"projectTaskNum": dataToPost.tasknum
			};

			const apiUrl = '{0}Project?db={1}'.format(config.data.authentication.api, config.data.authentication.apiSource);

			// Here we pass a date time stamp to ensure the latest config is always loaded
			axios
				.post(apiUrl, postBody)
				.then(async function (response) {
					// console.log('Create Project Response', response);

					let postStatus = true;

					if (postCreationTask) {
						if (configuration.element.postCreationTask.type.toLowerCase() === "samgis") {

							// Set the Project ID to be Posted (SAM GIS considers project_number as ProjectID)
							dataToPost.id = response.data.projectID;
							await samGIS(dataToPost)
								.then(function (message) {
									// console.log('Message:', message);

									if (message.status !== 'success') {
										postStatus = false;
									}
								});
						}
					}

					if (postStatus) {
						dispatch(appSetAlert(null, '{0} was successfully added.'.format(dataToPost.name), 'success', 10000));
					} else if (configuration.element.postCreationTask.type.toLowerCase() === "samgis") {
						dispatch(appSetAlert(null, '{0} was successfully added but the GIS initialization had an error.'.format(dataToPost.name), 'warning', 10000));
					}

					// Refresh Dependant Data Sources
					dispatch(dataReloadDependants(["vwClientProjects"]));

					// Close the dialog and signal a reload
					handleClose();

				})
				.catch(function (error) {
					console.log('**ERROR**', error);

					setShowProgress(false);

					dispatch(appSetAlert('Whoops!', 'Something went seriously wrong. Please try again.', 'error', 10000));

					// TODO: Reset state of form
					// TODO: Report issue on form
				});

		}, JSON.parse(axiosDelay()));
	};

	async function samGIS(dataToPost) {

		let message = {
			status: null,
			value: null
		}

		const validUsers = await getSAMGISUsers();

		const postBody = {
			"setup": {
				"project_name": dataToPost.name,
				"project_number": dataToPost.id,
				"utm_zone": "utmzone",
				"users": validUsers
			}
		};

		const apiUrl = configuration.element.postCreationTask.path;

		// Here we pass a date time stamp to ensure the latest config is always loaded
		await axios.post(apiUrl, postBody)
			.then(function (response) {
				// console.log('SAM GIS Response', response);

				message.status = 'success';
				message.value = response;
			})
			.catch(function (error) {
				console.log('**SAM GIS ERROR**', error);

				message.status = 'error';
				message.value = error;
			});

		return message;
	}

	async function getSAMGISUsers() {
		const users = [];

		const apiUrl = "{0}DBQuery?table=vwUserRole&where=RoleID INN (1,2)&fields=EMail&db={1}".format(config.data.authentication.api, config.data.authentication.apiSource);
		await axios.get('{0}'.format(apiUrl))
			.then(function (response) {
				// console.log('SAM GIS Users Response', response);

				for (const user of response.data) {
					users.push({value: user.eMail});
				}
			})
			.catch(function (error) {
				console.log('**SAM GIS Users ERROR**', error);
			});

		return users;
	}

	// const onClientChange = async (value) => {
	// 	await trigger(['client']);
	// 	setClientValue(value);
	// };

	return (
        <Root className={classes.root}>
			<div className={classes.card}>
				{showProgress && dataSource.fetching &&
					<LinearProgress className={classes.linearProgress}/>
				}
				{showProgress && !dataSource.fetching &&
					<LinearProgress className={classes.linearProgress}/>
				}
				<form className={classes.form} onSubmit={handleSubmit(onSubmit)}>
					<FormControl variant="standard" className={classes.margin}>
						<InputLabel htmlFor="input-with-icon-adornment">Project Name</InputLabel>
						<Input
							{...register("name")}
							name="name"
							id="name"
							startAdornment={
								<InputAdornment position="start">
									<StarIcon />
								</InputAdornment>
							}
							inputProps={{
								"aria-label": "Name"
							}}
						/>
						{errors.name && <p style={{color: 'red'}}>Project Name is a required field.</p>}
					</FormControl>
					{clientValues.length > 0 &&
						<FormControl variant="standard" className={classes.margin}>
							<InputLabel shrink htmlFor="input-with-icon-adornment">Client Name</InputLabel>
							<Select
								variant="standard"
								{...register("client")}
								name="client"
								id="client"
								displayEmpty
								// value={clientValue !== undefined ? clientValue : clientValues[0].value}
								// onChange={(e) => onClientChange(e.target.value)}
								input={
									<Input
										name="client"
										id="outlined-client-always-notched"
									/>
								}>
								{clientValues.map((r, index) =>
									<MenuItem key={index} value={r.value} primarytext={r.label}>{r.label}</MenuItem>
								)}
							</Select>
							{errors.client && <p style={{color: 'red'}}>Client is a required field.</p>}
						</FormControl>
					}
					<FormControl variant="standard" className={classes.margin}>
						<InputLabel htmlFor="input-with-icon-adornment">Project Description</InputLabel>
						<Input
							{...register("description")}
							name="description"
							id="description"
							startAdornment={
								<InputAdornment position="start">
									<SubjectIcon />
								</InputAdornment>
							}
							inputProps={{
								"aria-label": "Description"
							}}
						/>
						{errors.description && <p style={{color: 'red'}}>Project Description is a required field.</p>}
					</FormControl>
					<FormControl variant="standard" className={classes.margin}>
						<InputLabel htmlFor="input-with-icon-adornment">Project Number</InputLabel>
						<Input
							{...register("number")}
							name="number"
							id="number"
							startAdornment={
								<InputAdornment position="start">
									<SortIcon />
								</InputAdornment>
							}
							inputProps={{
								"aria-label": "Project Number"
							}}
						/>
						{errors.number && <p style={{color: 'red'}}>Project Number is a required field.</p>}
					</FormControl>
					<FormControl variant="standard" className={classes.margin}>
						<InputLabel htmlFor="input-with-icon-adornment">Task Number</InputLabel>
						<Input
							{...register("tasknum")}
							name="tasknum"
							id="tasknum"
							startAdornment={
								<InputAdornment position="start">
									<FilterListIcon />
								</InputAdornment>
							}
							inputProps={{
								"aria-label": "Task Number"
							}}
						/>
						{errors.tasknum && <p style={{color: 'red'}}>Task Number is a required field.</p>}
					</FormControl>
					<div className={classes.formEmbeddedActions}>
						<Button color={isThemeLight ? "primary" : "default"} onClick={() =>{ handleClose() }} disabled={formState.isSubmitting}>
							Cancel
						</Button>
						<Button color={isThemeLight ? "primary" : "default"} type="submit" disabled={formState.isSubmitting}>
							Submit
						</Button>
					</div>
				</form>
			</div>
		</Root>
    );
}